import './draw.css';
import { AudioSystem } from '../../shared/audio';
import { mountMuteButton, unlockAudioOnFirstPointer } from '../../shared/audioControls';
import { getElement } from '../../shared/dom';
import { format, initI18n, tr } from '../../shared/i18n';
import { readJson, writeJson } from '../../shared/storage';
import { FIGURES } from './figures';
import { dropConfetti } from '../../shared/confetti';
import { neighbours, startVertices, type Figure } from './euler';

/**
 * One-stroke puzzles. The maths lives in `euler.ts`; this file owns the canvas,
 * the pointer and the gallery.
 */

/** Edge n is drawn in colour n mod 7, so the finished figure is a rainbow. */
const RAINBOW = ['#e23b3b', '#ef8a2b', '#f2c300', '#3fae5c', '#2f8fd6', '#3b4fc0', '#8e4fc9'];
const MARGIN = 0.08;
/**
 * How long the finished figure is left uncovered before the popup arrives.
 * The rainbow drawing is the whole reward; the overlay sits on top of it, so
 * the confetti and the sound land first and the question of what to do next
 * waits a beat.
 */
const SAVOUR_MS = 1400;
const PROGRESS_KEY = 'fpc.draw';

interface Solved { solved: true; clean: boolean }

/**
 * Progress as stored, defended one field at a time. The value can be anything:
 * an older shape, something a curious child typed into devtools, or a half
 * written record. Anything that does not match is dropped rather than trusted,
 * because the cost of being wrong is a gallery that will not render at all.
 */
function readProgress(): Record<string, Solved> {
  const raw = readJson(PROGRESS_KEY);
  if (raw === null || typeof raw !== 'object' || Array.isArray(raw)) return {};
  const clean: Record<string, Solved> = {};
  for (const [id, value] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof value !== 'object' || value === null) continue;
    const record = value as { solved?: unknown; clean?: unknown };
    if (record.solved === true) clean[id] = { solved: true, clean: record.clean === true };
  }
  return clean;
}

declare global {
  interface Window {
    __DRAW_STATE__?: {
      figureId: string; at: number | null; drawn: number; total: number; solved: boolean; undos: number;
    };
    __DRAW_POINTS__?: Array<[number, number]>;
    /** The dots the hint is flashing, published so the tests can read them. */
    __DRAW_HINT__?: number[];
  }
}

interface Run {
  figure: Figure;
  /**
   * Vertices visited, in order; empty before the first tap. Keeping the whole
   * path rather than just the current dot is what makes Undo a pop instead of
   * a deduction about which end of the removed edge we came from.
   */
  path: number[];
  /** Edge indices drawn, in order. Always `path.length - 1` long. */
  trail: number[];
  undos: number;
}

/**
 * A stroked circle, drawn as a 32-sided polygon rather than an arc.
 *
 * A full-sweep arc is a degenerate path and the engines disagree about it,
 * each in a way that leaves a child with no visible hint. Measured on the same
 * canvas, same radius, same line width:
 *
 *   arc + stroke              Chrome 154: start point only   WebKit: hairline
 *   arc + closePath + stroke  Chrome 154: correct ring       WebKit: nothing
 *   two arcs filled           Chrome 154: correct ring       WebKit: nothing
 *   this polygon              Chrome 154: 5268 px            WebKit: 5294 px
 *
 * Straight segments are the one path primitive both draw identically, and at
 * this radius 32 of them are a circle to the eye.
 */
function strokeRing(ctx: CanvasRenderingContext2D, x: number, y: number, radius: number): void {
  const STEPS = 32;
  ctx.beginPath();
  for (let i = 0; i <= STEPS; i += 1) {
    const angle = (i / STEPS) * Math.PI * 2;
    const px = x + radius * Math.cos(angle);
    const py = y + radius * Math.sin(angle);
    if (i === 0) ctx.moveTo(px, py);
    else ctx.lineTo(px, py);
  }
  ctx.stroke();
}

/** The dot the child is standing on, or null before they have chosen one. */
const standingOn = (run: Run): number | null => run.path[run.path.length - 1] ?? null;

class DrawGame {
  private readonly canvas = getElement<HTMLCanvasElement>('#draw-canvas');
  private readonly context = this.canvas.getContext('2d') as CanvasRenderingContext2D;
  private readonly audio = new AudioSystem(() => Math.random());
  private run: Run = { figure: FIGURES[0], path: [], trail: [], undos: 0 };
  /** Vertices the hint is currently ringing. */
  private flash: number[] = [];
  private progress: Record<string, Solved> = readProgress();
  /** Pending hint timer, so switching puzzles can cancel it. */
  private flashTimer = 0;
  /** Pending win popup, so leaving the puzzle can call it off. */
  private winTimer = 0;

  constructor() {
    const wanted = new URLSearchParams(location.search).get('figure');
    const chosen = FIGURES.find((figure) => figure.id === wanted);
    if (chosen) this.run = { figure: chosen, path: [], trail: [], undos: 0 };

    this.resize();
    window.addEventListener('resize', () => this.resize());
    this.bind();
    this.buildGallery();
    this.render();
  }

  private bind(): void {
    const at = (event: PointerEvent): number | null => {
      const box = this.canvas.getBoundingClientRect();
      return this.vertexAt(event.clientX - box.left, event.clientY - box.top);
    };

    this.canvas.addEventListener('pointerdown', (event) => {
      const hit = at(event);
      if (hit !== null) this.choose(hit);
    });

    /**
     * Dragging across joined dots draws them in turn. The game is called One
     * Stroke; dragging is the gesture it invites, and `touch-action: none`
     * means a drag that does nothing leaves the page dead under the finger.
     * Tapping still works, which is what makes a slipped finger harmless.
     */
    this.canvas.addEventListener('pointermove', (event) => {
      if (event.buttons === 0) return;
      const hit = at(event);
      if (hit !== null && hit !== standingOn(this.run)) this.choose(hit);
    });

    getElement('#draw-undo').addEventListener('click', () => this.undo());
    getElement('#draw-restart').addEventListener('click', () => this.restart());
    getElement('#draw-hint').addEventListener('click', () => this.showHint());
    getElement('#draw-again').addEventListener('click', () => this.restart());
    getElement('#draw-next').addEventListener('click', () => {
      const at = FIGURES.findIndex((figure) => figure.id === this.run.figure.id);
      this.load(FIGURES[(at + 1) % FIGURES.length]);
    });

    unlockAudioOnFirstPointer(this.audio);
    mountMuteButton(getElement('#draw-mute'), this.audio);
  }

  /** The vertex under a pointer, within a target wide enough for a small finger. */
  private vertexAt(x: number, y: number): number | null {
    const size = this.canvas.clientWidth;
    const reach = Math.max(22, size * 0.07);
    let best: number | null = null;
    let bestDistance = reach;
    this.run.figure.vertices.forEach((vertex, index) => {
      const [vx, vy] = this.toCanvas(vertex);
      const distance = Math.hypot(vx - x, vy - y);
      if (distance < bestDistance) {
        best = index;
        bestDistance = distance;
      }
    });
    return best;
  }

  private choose(vertex: number): void {
    const { figure, trail } = this.run;
    const at = standingOn(this.run);

    const setStart = (): void => {
      this.run = { ...this.run, path: [vertex] };
      this.audio.select();
      this.say(format(tr().draw.edgesLeft, { count: figure.edges.length }));
      this.render();
    };

    if (at === null) {
      setStart();
      return;
    }
    if (vertex === at) return;

    const joined = neighbours(figure, at).filter((edge) => edge.to === vertex);
    const step = joined.find((edge) => !trail.includes(edge.edge));

    // Drawing comes first. Only when there is no line to draw does the tap mean
    // something else — and before anything is committed, what it means is "start
    // here instead". That matters: on a figure with two odd vertices every other
    // start is a dead end, and a child who picked wrong had no way back. Undo
    // has nothing to take back yet, and nothing on screen points at Start again.
    if (!step) {
      if (trail.length === 0) {
        setStart();
        return;
      }
      // "Those dots are not joined" is plainly false when a line is drawn
      // between them; the child can see it. Say which rejection it really is.
      this.say(joined.length > 0 ? tr().draw.alreadyDrawn : tr().draw.notAdjacent);
      return;
    }

    this.run = { ...this.run, path: [...this.run.path, vertex], trail: [...trail, step.edge] };
    this.audio.step();
    this.afterMove();
  }

  private undo(): void {
    if (this.run.trail.length === 0) return;
    this.closeOverlay();
    this.run = {
      ...this.run,
      path: this.run.path.slice(0, -1),
      trail: this.run.trail.slice(0, -1),
      undos: this.run.undos + 1,
    };
    this.audio.undo();
    this.afterMove();
  }

  private restart(): void {
    this.clearHint();
    this.closeOverlay();
    this.run = { figure: this.run.figure, path: [], trail: [], undos: 0 };
    this.say(tr().draw.tapToStart);
    this.afterMove();
  }

  private afterMove(): void {
    const left = this.run.figure.edges.length - this.run.trail.length;
    getElement<HTMLButtonElement>('#draw-undo').disabled = this.run.trail.length === 0;
    if (this.run.trail.length === 0) {
      // Undo back to an empty figure: the old count is now a lie on screen.
      this.say(tr().draw.tapToStart);
    } else if (left > 0) {
      this.say(format(tr().draw.edgesLeft, { count: left }));
    }
    this.render();

    if (this.run.trail.length === this.run.figure.edges.length) {
      this.win();
      return;
    }
    if (this.isStuck()) this.say(tr().draw.stuck);
  }

  private showHint(): void {
    const dots = startVertices(this.run.figure);
    window.__DRAW_HINT__ = dots;
    const everywhere = dots.length === this.run.figure.vertices.length;
    this.say(everywhere ? tr().draw.startAnywhere : tr().draw.startHere);
    this.flash = dots;
    this.render();
    window.clearTimeout(this.flashTimer);
    this.flashTimer = window.setTimeout(() => this.clearHint(), 2200);
  }

  /**
   * Rings and the published answer belong to one figure. Carrying them into
   * another is worse than never hinting: the indices stay valid and point at
   * dots that are not legal starts there, so a child who trusts them is
   * guaranteed to strand themselves.
   */
  /** Drops a win popup that has not arrived yet, and hides one that has. */
  private closeOverlay(): void {
    window.clearTimeout(this.winTimer);
    this.winTimer = 0;
    getElement('#draw-overlay').classList.add('hidden');
  }

  private clearHint(): void {
    window.clearTimeout(this.flashTimer);
    this.flashTimer = 0;
    this.flash = [];
    window.__DRAW_HINT__ = [];
    this.render();
  }

  /** Still edges to draw, but none of them leave the dot we are standing on. */
  private isStuck(): boolean {
    const { figure, trail } = this.run;
    const at = standingOn(this.run);
    if (at === null || trail.length === figure.edges.length) return false;
    return !neighbours(figure, at).some((edge) => !trail.includes(edge.edge));
  }

  private win(): void {
    const clean = this.run.undos === 0;
    // The line under the canvas still read "1 left to draw" from the move
    // before this one, beside a figure that is finished.
    this.say(tr().draw.winBody);
    getElement('#draw-win-body').textContent =
      clean ? `${tr().draw.winBody} ${tr().draw.winClean}` : tr().draw.winBody;

    // Celebrate at once, ask what next in a moment. Everything that is not the
    // popup happens now, so a child who moves on during the pause still keeps
    // the puzzle they solved.
    // `layer` defaults to '#confetti-layer', which the template already has.
    dropConfetti();
    this.audio.victory();
    window.clearTimeout(this.winTimer);
    this.winTimer = window.setTimeout(() => {
      getElement('#draw-overlay').classList.remove('hidden');
      getElement<HTMLButtonElement>('#draw-next').focus();
    }, SAVOUR_MS);

    const already = this.progress[this.run.figure.id];
    this.progress = {
      ...this.progress,
      // A clean run is never taken back by a later messy one.
      [this.run.figure.id]: { solved: true, clean: clean || (already?.clean ?? false) },
    };
    writeJson(PROGRESS_KEY, this.progress);
    this.buildGallery();
  }

  private buildGallery(): void {
    const host = getElement('#draw-gallery');
    host.replaceChildren();
    FIGURES.forEach((figure) => {
      const done = this.progress[figure.id];
      const tile = document.createElement('button');
      tile.type = 'button';
      tile.className = 'figure-tile';
      tile.dataset.figure = figure.id;
      tile.dataset.solved = done ? 'true' : 'false';
      const names = tr().draw.names as Record<string, string>;
      const label = format(tr().draw.tileLabel, {
        n: FIGURES.indexOf(figure) + 1,
        name: names[figure.id] ?? figure.id,
      });
      tile.setAttribute('aria-label', `${label} — ${done ? tr().draw.solved : tr().draw.locked}`);
      tile.title = label;
      if (figure.id === this.run.figure.id) tile.setAttribute('aria-current', 'true');

      const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
      svg.setAttribute('viewBox', '0 0 100 100');
      figure.edges.forEach(([a, b], index) => {
        const line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
        line.setAttribute('x1', String(figure.vertices[a][0]));
        line.setAttribute('y1', String(figure.vertices[a][1]));
        line.setAttribute('x2', String(figure.vertices[b][0]));
        line.setAttribute('y2', String(figure.vertices[b][1]));
        line.setAttribute('stroke', done ? RAINBOW[index % RAINBOW.length] : '#c3cedd');
        line.setAttribute('stroke-width', '5');
        line.setAttribute('stroke-linecap', 'round');
        svg.append(line);
      });
      tile.append(svg);

      if (done?.clean) {
        const star = document.createElement('span');
        star.className = 'star';
        star.textContent = '⭐';
        // Decoration only: the tile's aria-label already says it is solved, and
        // a star inside the button would otherwise become the button's name.
        star.setAttribute('aria-hidden', 'true');
        tile.append(star);
      }

      tile.addEventListener('click', () => this.load(figure));
      host.append(tile);
    });
  }

  private load(figure: Figure): void {
    this.clearHint();
    this.closeOverlay();
    this.run = { figure, path: [], trail: [], undos: 0 };
    this.say(tr().draw.tapToStart);
    this.buildGallery();
    this.afterMove();
  }

  private say(message: string): void {
    getElement('#draw-status').textContent = message;
  }

  /** Canvas pixels for a 0-100 grid point, inset by a margin so dots are not clipped. */
  private toCanvas([x, y]: [number, number]): [number, number] {
    const size = this.canvas.width / devicePixelRatio;
    const inner = size * (1 - 2 * MARGIN);
    return [size * MARGIN + (x / 100) * inner, size * MARGIN + (y / 100) * inner];
  }

  private resize(): void {
    const css = this.canvas.clientWidth;
    this.canvas.width = css * devicePixelRatio;
    this.canvas.height = css * devicePixelRatio;
    this.context.setTransform(devicePixelRatio, 0, 0, devicePixelRatio, 0, 0);
    this.render();
  }

  private render(): void {
    const size = this.canvas.width / devicePixelRatio;
    const ctx = this.context;
    ctx.clearRect(0, 0, size, size);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.lineWidth = Math.max(4, size * 0.018);

    const { figure, trail } = this.run;
    const at = standingOn(this.run);
    const order = new Map(trail.map((edge, index) => [edge, index]));

    figure.edges.forEach(([a, b], index) => {
      const [x1, y1] = this.toCanvas(figure.vertices[a]);
      const [x2, y2] = this.toCanvas(figure.vertices[b]);
      const drawnAt = order.get(index);
      ctx.strokeStyle = drawnAt === undefined ? '#c3cedd' : RAINBOW[drawnAt % RAINBOW.length];
      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.stroke();
    });

    figure.vertices.forEach((vertex, index) => {
      const [x, y] = this.toCanvas(vertex);
      if (this.flash.includes(index)) {
        ctx.strokeStyle = '#ff8a5c';
        strokeRing(ctx, x, y, size * 0.042);
      }
      ctx.beginPath();
      ctx.arc(x, y, size * 0.022, 0, Math.PI * 2);
      ctx.closePath();
      ctx.fillStyle = index === at ? '#ff8a5c' : '#2b4a6f';
      ctx.fill();
      ctx.lineWidth = Math.max(2, size * 0.007);
      ctx.strokeStyle = '#fff';
      ctx.stroke();
      ctx.lineWidth = Math.max(4, size * 0.018);
    });

    this.publish();
  }

  private publish(): void {
    const { figure, trail, undos } = this.run;
    const position = FIGURES.findIndex((entry) => entry.id === figure.id) + 1;
    getElement('#draw-title').textContent = format(tr().draw.puzzleOf, {
      n: position,
      total: FIGURES.length,
    });
    window.__DRAW_STATE__ = {
      figureId: figure.id,
      at: standingOn(this.run),
      drawn: trail.length,
      total: figure.edges.length,
      solved: trail.length === figure.edges.length,
      undos,
    };
    window.__DRAW_POINTS__ = figure.vertices.map((vertex) => this.toCanvas(vertex));
  }
}

void initI18n().then(() => new DrawGame());
